import { Notification as AppNotification, NotificationType } from '@/lib/types/notifications';

export class DesktopNotificationService {
  private static instance: DesktopNotificationService;
  private permission: NotificationPermission = 'default';
  private isSupported: boolean = false;

  private constructor() {
    this.isSupported = 'Notification' in window;
    if (this.isSupported) {
      this.permission = Notification.permission;
    }
  }

  public static getInstance(): DesktopNotificationService {
    if (!DesktopNotificationService.instance) {
      DesktopNotificationService.instance = new DesktopNotificationService();
    }
    return DesktopNotificationService.instance;
  }

  /**
   * Check if desktop notifications are supported
   */
  public isDesktopNotificationSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Get current notification permission status
   */
  public getPermissionStatus(): NotificationPermission {
    return this.permission;
  }

  /**
   * Request permission for desktop notifications
   */
  public async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported) {
      console.warn('Desktop notifications are not supported in this browser');
      return 'denied';
    }

    if (this.permission === 'granted') {
      return this.permission;
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  /**
   * Show a desktop notification
   */
  public async showNotification(
    title: string,
    options: {
      body?: string;
      icon?: string;
      badge?: string;
      tag?: string;
      data?: any;
      requireInteraction?: boolean;
      silent?: boolean;
      actions?: NotificationAction[];
    } = {}
  ): Promise<Notification | null> {
    if (!this.isSupported) {
      console.warn('Desktop notifications are not supported');
      return null;
    }

    if (this.permission !== 'granted') {
      console.warn('Desktop notification permission not granted');
      return null;
    }

    try {
      const notification = new Notification(title, {
        icon: options.icon || '/icon-192.png',
        badge: options.badge || '/icon-192.png',
        body: options.body,
        tag: options.tag,
        data: options.data,
        requireInteraction: options.requireInteraction || false,
        silent: options.silent || false,
        ...options
      });

      // Auto-close after 10 seconds unless requireInteraction is true
      if (!options.requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 10000);
      }

      return notification;
    } catch (error) {
      console.error('Error showing desktop notification:', error);
      return null;
    }
  }

  /**
   * Show notification from app notification data
   */
  public async showNotificationFromAppData(
    appNotification: AppNotification,
    options: {
      onClick?: (notification: Notification) => void;
      onClose?: (notification: Notification) => void;
      onError?: (error: Event) => void;
    } = {}
  ): Promise<Notification | null> {
    const desktopNotification = await this.showNotification(
      appNotification.title,
      {
        body: appNotification.message,
        tag: appNotification.id,
        data: {
          ...appNotification.data,
          actionUrl: appNotification.actionUrl,
          notificationId: appNotification.id
        },
        requireInteraction: this.shouldRequireInteraction(appNotification.type),
        icon: this.getIconForNotificationType(appNotification.type)
      }
    );

    if (desktopNotification) {
      // Handle click event
      desktopNotification.onclick = (event) => {
        event.preventDefault();
        desktopNotification.close();
        
        // Focus the window if it's not focused
        if (window.focus) {
          window.focus();
        }

        // Custom click handler
        if (options.onClick) {
          options.onClick(desktopNotification);
        }

        // Navigate to action URL if provided
        if (appNotification.actionUrl) {
          window.location.href = appNotification.actionUrl;
        }
      };

      // Handle close event
      desktopNotification.onclose = (event) => {
        if (options.onClose) {
          options.onClose(desktopNotification);
        }
      };

      // Handle error event
      desktopNotification.onerror = (error) => {
        console.error('Desktop notification error:', error);
        if (options.onError) {
          options.onError(error);
        }
      };
    }

    return desktopNotification;
  }

  /**
   * Get appropriate icon for notification type
   */
  private getIconForNotificationType(type: NotificationType): string {
    const iconMap: Record<NotificationType, string> = {
      // Task & Calendar
      [NotificationType.TaskAssigned]: '/icon-512.png',
      [NotificationType.TaskUpdated]: '/icon-512.png',
      [NotificationType.TaskCompleted]: '/icon-512.png',
      [NotificationType.TaskDue]: '/icon-512.png',
      [NotificationType.EventInvite]: '/icon-512.png',
      [NotificationType.EventUpdated]: '/icon-512.png',
      
      // Templates
      [NotificationType.TemplateReview]: '/icon-512.png',
      [NotificationType.TemplateCorrections]: '/icon-512.png',
      [NotificationType.TemplateApproved]: '/icon-512.png',
      [NotificationType.TemplateRejected]: '/icon-512.png',
      
      // Photo Log
      [NotificationType.PhotosUploaded]: '/icon-512.png',
      [NotificationType.PhotosProcessed]: '/icon-512.png',
      
      // FIN Chat
      [NotificationType.FinChatMessage]: '/icon-512.png',
      [NotificationType.FinChatMention]: '/icon-512.png',

      // Repairs
      [NotificationType.RepairQuoteReady]: '/icon-512.png',
      
      // Jewelry Requests
      [NotificationType.JewelryRequestSubmitted]: '/icon-512.png',
      
      // Content Creation
      [NotificationType.ContentSubmitted]: '/icon-512.png',
      [NotificationType.ContentApproved]: '/icon-512.png',
      [NotificationType.ContentDenied]: '/icon-512.png',
      [NotificationType.ContentReworkRequested]: '/icon-512.png',
      
      // System
      [NotificationType.SystemUpdate]: '/icon-512.png',
      [NotificationType.SystemMaintenance]: '/icon-512.png',
      
      // General
      [NotificationType.General]: '/icon-512.png'
    };

    return iconMap[type] || '/icon-512.png';
  }

  /**
   * Determine if notification should require interaction
   */
  private shouldRequireInteraction(type: NotificationType): boolean {
    // High priority notifications that should require interaction
    const highPriorityTypes = [
      NotificationType.TaskDue,
      NotificationType.SystemMaintenance,
      NotificationType.TemplateReview,
      NotificationType.FinChatMention
    ];

    return highPriorityTypes.includes(type);
  }

  /**
   * Clear all notifications with a specific tag
   */
  public async clearNotificationsWithTag(tag: string): Promise<void> {
    if (!this.isSupported || !('serviceWorker' in navigator)) {
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const notifications = await registration.getNotifications({ tag });
      
      notifications.forEach(notification => {
        notification.close();
      });
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  }

  /**
   * Clear all notifications
   */
  public async clearAllNotifications(): Promise<void> {
    if (!this.isSupported || !('serviceWorker' in navigator)) {
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const notifications = await registration.getNotifications();
      
      notifications.forEach(notification => {
        notification.close();
      });
    } catch (error) {
      console.error('Error clearing all notifications:', error);
    }
  }

  /**
   * Check if user prefers reduced motion (for notification animations)
   */
  public prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

export const desktopNotificationService = DesktopNotificationService.getInstance(); 